import { POLICIES } from '@v2/commons/config'
import {
  RouteConfig,
  DEFAULT_ROUTES as default_routes,
} from '@v2/domains/routing/types/routing.types'

export const PUBLIC_ROUTES_CONFIG: Record<
  string,
  Omit<RouteConfig, 'element'>
> = {
  LOGIN: {
    key: 'LOGIN',
    path: '/login',
    requiresAuth: false,
    label: 'Login',
    showInMenu: false,
  },
  FORGOT_PASSWORD: {
    key: 'FORGOT_PASSWORD',
    path: '/forgot',
    requiresAuth: false,
    label: 'Forgot my password',
    showInMenu: false,
  },
  SELECT_ORGANIZATION: {
    key: 'SELECT_ORGANIZATION',
    path: '/select-organization',
    requiresAuth: true,
    label: 'Select Organization',
    showInMenu: false,
  },
  RESET_PASSWORD: {
    key: 'RESET_PASSWORD',
    path: '/reset-password',
    requiresAuth: false,
    label: 'Reset Password',
    showInMenu: false,
  },
}

export const DEFAULT_ROUTES_CONFIG: Record<
  string,
  Omit<RouteConfig, 'element'>
> = {
  [default_routes.DASHBOARD]: {
    key: 'DASHBOARD',
    path: '/dashboard',
    requiresAuth: true,
    label: 'Dashboard',
    policy: 'is_get_dashboard_enabled',
    showInMenu: false,
    menuConfig: {
      icon: 'Reports',
      order: 1,
      platforms: ['default'],
    },
  },
  [default_routes.LEADS]: {
    key: 'LEADS',
    path: '/leads',
    requiresAuth: true,
    label: 'Leads',
    policy: 'is_get_persons_enabled',
    showInMenu: true,
    menuConfig: {
      icon: 'User',
      order: 3,
      platforms: ['default'],
    },
  },
  [default_routes.LEAD_DETAIL]: {
    key: 'LEAD_DETAIL',
    path: '/leads/:personId',
    requiresAuth: true,
    label: 'Lead Detail',
    policy: 'is_get_persons_enabled',
    showInMenu: false,
    parentKey: 'LEADS',
  },
  [default_routes.LEAD_TICKET_DETAIL]: {
    key: 'LEAD_DETAIL',
    path: '/leads/:personId/:ticketId',
    requiresAuth: true,
    label: 'Lead Detail',
    policy: 'is_get_persons_enabled',
    showInMenu: false,
    parentKey: 'LEADS',
  },
  [default_routes.LEAD_CREATE]: {
    key: 'LEAD_CREATE',
    path: '/leads/create',
    requiresAuth: true,
    label: 'Lead Detail',
    policy: 'is_get_persons_enabled',
    showInMenu: false,
    parentKey: 'LEADS',
  },
  [default_routes.ACCOUNTS]: {
    key: 'ACCOUNTS',
    path: '/accounts',
    requiresAuth: true,
    label: 'Accounts',
    policy: 'is_get_persons_enabled',
    showInMenu: true,
    menuConfig: {
      icon: 'Account',
      order: 4,
      platforms: ['default'],
    },
  },
  [default_routes.ACCOUNT_DETAIL]: {
    key: 'ACCOUNT_DETAIL',
    path: '/accounts/:personId',
    requiresAuth: true,
    label: 'Account Detail',
    policy: 'is_get_persons_enabled',
    showInMenu: false,
    parentKey: 'ACCOUNTS',
  },
  [default_routes.ACCOUNT_TICKET_DETAIL]: {
    key: 'ACCOUNT_DETAIL_',
    path: '/accounts/:personId/:ticketId',
    requiresAuth: true,
    label: 'Account Detail',
    policy: 'is_get_persons_enabled',
    showInMenu: false,
    parentKey: 'ACCOUNTS',
  },
  [default_routes.MERGING_TOOL]: {
    key: 'MERGING_TOOL',
    path: '/merging-tool',
    requiresAuth: true,
    label: 'Merging tool',
    policy: 'is_get_persons_enabled',
    showInMenu: false,
    parentKey: 'ACCOUNTS',
  },
  [default_routes.TICKETS]: {
    key: 'TICKETS',
    path: '/tickets',
    requiresAuth: true,
    label: 'Tickets',
    policy: 'is_get_office_visits_by_organization_id_enabled',
    showInMenu: true,
    menuConfig: {
      icon: 'Ticket',
      order: 2,
      platforms: ['default'],
    },
  },
  [default_routes.TASKS]: {
    key: 'TASKS',
    path: '/tasks',
    requiresAuth: true,
    label: 'Tasks',
    policy: 'is_get_tasks_enabled',
    showInMenu: true,
    menuConfig: {
      icon: 'Page',
      order: 9,
      platforms: ['default'],
    },
  },
  [default_routes.ADMINISTRATION]: {
    key: 'ADMINISTRATION',
    path: '/administration',
    requiresAuth: true,
    label: 'Administration',
    policy: 'is_enable_administration',
    showInMenu: true,
    menuConfig: {
      icon: 'Benefit',
      order: 10,
    },
  },
  [default_routes.ADMINISTRATION_ABCS]: {
    key: 'ADMINISTRATION_ABCS',
    path: '/administration/abcs',
    requiresAuth: true,
    label: 'Administration',
    policy: 'is_enable_administration',
    showInMenu: false,
    parentKey: 'ADMINISTRATION',
  },
  [default_routes.ADMINISTRATION_BENEFITS]: {
    key: 'ADMINISTRATION_BENEFITS',
    path: '/administration-benefits',
    requiresAuth: true,
    label: 'Benefits Administration',
    policy: 'is_enable_benefits_administration',
    showInMenu: true,
    parentKey: 'ADMINISTRATION',
    menuConfig: {
      icon: 'Benefit',
      order: 6,
    },
  },
  [default_routes.ADMINISTRATION_BENEFITS_PREVIEW]: {
    key: 'ADMINISTRATION_BENEFITS_PREVIEW',
    path: '/administration-benefits/preview',
    requiresAuth: true,
    label: 'Administration',
    policy: 'is_enable_administration',
    showInMenu: false,
    parentKey: 'ADMINISTRATION',
  },
  [default_routes.ADMINISTRATION_BENEFITS_DETAIL]: {
    key: 'ADMINISTRATION_BENEFITS_DETAIL',
    path: '/administration-benefits/:benefitId',
    requiresAuth: true,
    label: 'Administration',
    policy: 'is_enable_administration',
    showInMenu: false,
    parentKey: 'ADMINISTRATION',
  },
  [default_routes.ADMINISTRATION_BENEFITS_CREATE]: {
    key: 'ADMINISTRATION_BENEFITS_CREATE',
    path: '/administration-benefits/create',
    requiresAuth: true,
    label: 'Administration',
    policy: 'is_enable_administration',
    showInMenu: false,
    parentKey: 'ADMINISTRATION',
  },
  [default_routes.ADMINISTRATION_POLICIES]: {
    key: 'ADMINISTRATION_POLICIES',
    path: '/administration/policies',
    requiresAuth: true,
    label: 'Administration',
    policy: 'is_enable_administration',
    showInMenu: false,
    parentKey: 'ADMINISTRATION',
  },
  [default_routes.ADMINISTRATION_ROLES]: {
    key: 'ADMINISTRATION_ROLES',
    path: '/administration/roles',
    requiresAuth: true,
    label: 'Administration',
    policy: 'is_enable_administration',
    showInMenu: false,
    parentKey: 'ADMINISTRATION',
  },
  [default_routes.ADMINISTRATION_ROLES_DETAIL]: {
    key: 'ADMINISTRATION_ROLES_DETAIL',
    path: '/administration/roles/:roleId',
    requiresAuth: true,
    label: 'Administration',
    policy: 'is_enable_administration',
    showInMenu: false,
    parentKey: 'ADMINISTRATION',
  },
  [default_routes.ADMINISTRATION_TAGS]: {
    key: 'ADMINISTRATION_TAGS',
    path: '/administration/tags',
    requiresAuth: true,
    label: 'Administration',
    policy: 'is_enable_administration',
    showInMenu: false,
    parentKey: 'ADMINISTRATION',
  },
  [default_routes.ADMINISTRATION_USERS]: {
    key: 'ADMINISTRATION_USERS',
    path: '/administration/users',
    requiresAuth: true,
    label: 'Administration',
    policy: 'is_enable_administration',
    showInMenu: false,
    parentKey: 'ADMINISTRATION',
  },
  [default_routes.ADMINISTRATION_USERS_CREATE]: {
    key: 'ADMINISTRATION_USERS_CREATE',
    path: '/administration/users/create',
    requiresAuth: true,
    label: 'Administration',
    policy: 'is_enable_administration',
    showInMenu: false,
    parentKey: 'ADMINISTRATION',
  },
  [default_routes.ADMINISTRATION_USERS_DETAILS]: {
    key: 'ADMINISTRATION_USERS_DETAILS',
    path: '/administration/users/:userId',
    requiresAuth: true,
    label: 'Administration',
    policy: 'is_enable_administration',
    showInMenu: false,
    parentKey: 'ADMINISTRATION',
  },
  [default_routes.ADMINISTRATION_EMAIL_TEMPLATE]: {
    key: 'ADMINISTRATION_EMAIL_TEMPLATE',
    path: '/administration/email-template',
    requiresAuth: true,
    label: 'Administration',
    policy: 'is_enable_administration',
    showInMenu: false,
    parentKey: 'ADMINISTRATION',
  },
  [default_routes.ADMINISTRATION_EMAIL_TEMPLATE_DETAIL]: {
    key: 'ADMINISTRATION_EMAIL_TEMPLATE_DETAIL',
    path: '/administration/email-template/:emailTemplateId',
    requiresAuth: true,
    label: 'Administration',
    policy: 'is_enable_administration',
    showInMenu: false,
    parentKey: 'ADMINISTRATION',
  },
  [default_routes.ADMINISTRATION_EMAIL_TEMPLATE_CREATE]: {
    key: 'ADMINISTRATION_EMAIL_TEMPLATE_CREATE',
    path: '/administration/email-template/create',
    requiresAuth: true,
    label: 'Administration',
    policy: 'is_enable_administration',
    showInMenu: false,
    parentKey: 'ADMINISTRATION',
  },
  [default_routes.REPORTS]: {
    key: 'REPORTS',
    path: '/reports',
    requiresAuth: true,
    label: 'Reports',
    policy: 'is_enable_reports',
    showInMenu: true,
    menuConfig: {
      icon: 'Reports',
      order: 11,
      platforms: ['default'],
    },
  },
  [default_routes.DATA_PROCESSING]: {
    key: 'DATA_PROCESSING',
    path: '/data-processing',
    requiresAuth: true,
    label: 'Data Processing',
    policy: 'is_enable_data_processing',
    showInMenu: true,
    menuConfig: {
      icon: 'Reports',
      order: 12,
      platforms: ['default'],
    },
  },
  [default_routes.SEARCH]: {
    key: 'SEARCH',
    path: '/search/:searchValue',
    requiresAuth: true,
    label: '',
    policy: 'is_enable_data_processing',
    showInMenu: false,
  },
  [default_routes.SMS_CAMPAIGNS]: {
    key: 'SMS_CAMPAIGNS',
    path: '/sms-campaigns',
    requiresAuth: true,
    label: 'SMS Campaigns',
    policy: 'is_sms_campaigns_enabled',
    showInMenu: true,
    menuConfig: {
      icon: 'Sms',
      order: 7,
      platforms: ['default'],
    },
  },
  [default_routes.SMS_CAMPAIGNS_CREATE]: {
    key: 'SMS_CAMPAIGNS_CREATE',
    path: '/sms-campaigns/create',
    requiresAuth: true,
    label: 'SMS Campaigns',
    policy: 'is_sms_campaigns_enabled',
    showInMenu: false,
    parentKey: 'SMS_CAMPAIGNS',
  },
  [default_routes.SMS_CAMPAIGNS_DETAIL]: {
    key: 'SMS_CAMPAIGNS_DETAIL',
    path: '/sms-campaigns/:campaignId',
    requiresAuth: true,
    label: 'SMS Campaigns',
    policy: 'is_sms_campaigns_enabled',
    showInMenu: false,
    parentKey: 'SMS_CAMPAIGNS',
  },
  [default_routes.FORM_SUBMISSIONS]: {
    key: 'FORM_SUBMISSIONS',
    path: '/form-submissions',
    requiresAuth: true,
    label: 'Forms Submission',
    policy: POLICIES.IsEnableViewFormSubmissionsTab,
    showInMenu: true,
    menuConfig: {
      icon: 'PageDuplicate',
      order: 8,
      platforms: ['default'],
    },
  },
  [default_routes.FORM_SUBMISSIONS_DETAIL]: {
    key: 'FORM_SUBMISSIONS_DETAIL',
    path: '/form-submissions/:formId',
    requiresAuth: true,
    label: 'Forms Submission',
    policy: POLICIES.IsEnableViewFormSubmissionsTab,
    showInMenu: false,
    parentKey: 'FORM_SUBMISSIONS',
  },
  [default_routes.UPLOAD_FILE]: {
    key: 'UPLOAD_FILE',
    path: '/upload-file',
    requiresAuth: true,
    label: 'Upload File',
    policy: POLICIES.IsEnableViewFormSubmissionsTab,
    showInMenu: false,
  },
  [default_routes.TEAM_LEAD_HUD]: {
    key: 'TEAM_LEAD_HUD',
    path: '/team-lead-hud',
    requiresAuth: true,
    label: 'Team Lead Hud',
    policy: 'is_team_lead_hub_tab_enabled',
    showInMenu: true,
    menuConfig: {
      icon: 'TeamLeadHub',
      order: 5,
      platforms: ['default'],
    },
  },
  [default_routes.CLINICS]: {
    key: 'CLINICS',
    path: '/team-lead-hud/clinic',
    requiresAuth: true,
    label: 'Clinics',
    showInMenu: false,
  },
  [default_routes.CLINIC_DETAIL]: {
    key: 'CLINIC_DETAIL',
    path: '/team-lead-hud/clinic/:clinicId',
    requiresAuth: true,
    label: 'Clinic Detail',
    showInMenu: false,
  },
  [default_routes.LOCATIONS]: {
    key: 'LOCATIONS',
    path: '/team-lead-hud/locations',
    requiresAuth: true,
    label: 'Locations',
    showInMenu: false,
  },
}

export const BENESTREAM_ROUTES_CONFIG: Record<
  string,
  Omit<RouteConfig, 'element'>
> = {
  LEADS: {
    key: 'LEADS',
    path: '/leads',
    requiresAuth: true,
    label: 'Leads',
    policy: 'is_get_persons_enabled',
    showInMenu: true,
    menuConfig: {
      icon: 'Filter',
      order: 1,
      platforms: ['benestream'],
    },
  },
  LEAD_DETAIL: {
    key: 'LEAD_DETAIL',
    path: '/leads/:personId',
    requiresAuth: true,
    label: 'Lead Detail',
    policy: 'is_get_persons_enabled',
    showInMenu: false,
    parentKey: 'LEADS',
  },
  LEAD_CREATE: {
    key: 'LEAD_CREATE',
    path: '/leads/create',
    requiresAuth: true,
    label: 'Lead Detail',
    policy: 'is_get_persons_enabled',
    showInMenu: false,
    parentKey: 'LEADS',
  },
  ACCOUNTS: {
    key: 'ACCOUNTS',
    path: '/accounts',
    requiresAuth: true,
    label: 'Accounts',
    policy: 'is_get_persons_enabled',
    showInMenu: true,
    menuConfig: {
      icon: 'Account',
      order: 2,
      platforms: ['benestream'],
    },
  },
  ACCOUNT_DETAIL: {
    key: 'ACCOUNT_DETAIL',
    path: '/accounts/:personId',
    requiresAuth: true,
    label: 'Account Detail',
    policy: 'is_get_persons_enabled',
    showInMenu: false,
    parentKey: 'ACCOUNTS',
  },
  ADMINISTRATION: {
    key: 'ADMINISTRATION',
    path: '/administration',
    requiresAuth: true,
    label: 'Administration',
    policy: 'is_enable_administration',
    showInMenu: true,
    menuConfig: {
      icon: 'Benefit',
      order: 3,
      platforms: ['benestream'],
    },
  },
  SETTINGS: {
    key: 'SETTINGS',
    path: '/settings',
    requiresAuth: true,
    label: 'Settings',
    policy: null,
    showInMenu: true,
    menuConfig: {
      icon: 'Settings',
      order: 4,
      platforms: ['benestream'],
    },
  },
  DATA_LOADER: {
    key: 'DATA_LOADER',
    path: '/upload-file',
    requiresAuth: true,
    label: 'Upload File',
    policy: 'is_persons_data_loader_enabled',
    showInMenu: false,
  },
  SEARCH: {
    key: 'SEARCH',
    path: '/search/:searchValue',
    requiresAuth: true,
    label: 'Búsqueda',
    policy: null,
    showInMenu: false,
  },
  ADMINISTRATION_POLICIES: {
    key: 'ADMINISTRATION_POLICIES',
    path: '/administration/policies',
    requiresAuth: true,
    label: 'Administration',
    policy: 'is_enable_administration',
    showInMenu: false,
  },
  ADMINISTRATION_ROLES: {
    key: 'ADMINISTRATION_ROLES',
    path: '/administration/roles',
    requiresAuth: true,
    label: 'Administration',
    policy: 'is_enable_administration',
    showInMenu: false,
  },
  ADMINISTRATION_ROLES_DETAIL: {
    key: 'ADMINISTRATION_ROLES_DETAIL',
    path: '/administration/roles/:roleId',
    requiresAuth: true,
    label: 'Administration',
    policy: 'is_enable_administration',
    showInMenu: false,
  },
  ADMINISTRATION_TAGS: {
    key: 'ADMINISTRATION_TAGS',
    path: '/administration/tags',
    requiresAuth: true,
    label: 'Administration',
    policy: 'is_enable_administration',
    showInMenu: false,
  },
  ADMINISTRATION_USERS: {
    key: 'ADMINISTRATION_USERS',
    path: '/administration/users',
    requiresAuth: true,
    label: 'Administration',
    policy: 'is_enable_administration',
    showInMenu: false,
  },
  ADMINISTRATION_USERS_CREATE: {
    key: 'ADMINISTRATION_USERS_CREATE',
    path: '/administration/users/create',
    requiresAuth: true,
    label: 'Administration',
    policy: 'is_enable_administration',
    showInMenu: false,
  },
  ADMINISTRATION_USERS_DETAILS: {
    key: 'ADMINISTRATION_USERS_DETAILS',
    path: '/administration/users/:userId',
    requiresAuth: true,
    label: 'Administration',
    policy: 'is_enable_administration',
    showInMenu: false,
  },
  ADMINISTRATION_EMAIL_TEMPLATE: {
    key: 'ADMINISTRATION_EMAIL_TEMPLATE',
    path: '/administration/email-template',
    requiresAuth: true,
    label: 'Administration',
    policy: 'is_enable_administration',
    showInMenu: false,
  },
  ADMINISTRATION_EMAIL_TEMPLATE_DETAIL: {
    key: 'ADMINISTRATION_EMAIL_TEMPLATE_DETAIL',
    path: '/administration/email-template/:emailTemplateId',
    requiresAuth: true,
    label: 'Administration',
    policy: 'is_enable_administration',
    showInMenu: false,
  },
  ADMINISTRATION_EMAIL_TEMPLATE_CREATE: {
    key: 'ADMINISTRATION_EMAIL_TEMPLATE_CREATE',
    path: '/administration/email-template/create',
    requiresAuth: true,
    label: 'Administration',
    policy: 'is_enable_administration',
    showInMenu: false,
  },
}

export const CUSTOM_ROUTES_CONFIG: Omit<RouteConfig, 'element'> = {
  key: 'CUSTOM_PATH',
  path: '',
  requiresAuth: true,
  label: 'ROUTE FOR LEGACY NAVIGATION',
  showInMenu: false,
}

export const ALL_ROUTES: Record<string, Omit<RouteConfig, 'element'>> = {
  ...PUBLIC_ROUTES_CONFIG,
  ...DEFAULT_ROUTES_CONFIG,
  CUSTOM_PATH: CUSTOM_ROUTES_CONFIG,
}
